import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Factory, Check, ChevronDown, ChevronUp, MapPin, ArrowRight, Phone, Building2, Square, Sofa } from 'lucide-react';

const WarehouseIndustrialCleaning = ({ onQuoteClick }) => {
  const [openFaq, setOpenFaq] = useState(null);

  const faqs = [
    {
      q: "What's involved in a commercial end-of-lease warehouse clean?",
      a: "It depends on the site and what the lease requires, but typically covers high-pressure cleaning, rotary concrete floor cleaning, removal of old paint and line markings, dust and oil removal, plus carpet, toilets, windows and the carpark. We scope it to your property and handover timeline."
    },
    {
      q: "Do you offer ongoing industrial cleaning, or only one-off jobs at lease end?",
      a: "Both. Most of our warehouse work is one-off end-of-lease turnovers, but we also provide ongoing exterior high-pressure cleaning for operating sites, including regular work for KFC locations across WA."
    },
    {
      q: "Can you clean concrete floors with old paint or line markings on them?",
      a: "Yes, rotary concrete floor cleaning and paint removal is part of what we do regularly on warehouse and industrial sites, restoring the floor rather than just cleaning around what's already there."
    },
    {
      q: "Do you work with government or mining sites?",
      a: "We currently work with local government on commercial and industrial cleaning, and we're actively working toward ISO certification to take on larger government and mining contracts as the business grows."
    }
  ];

  // Service Schema for SEO
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Warehouse & Industrial Cleaning",
    "provider": {
      "@type": "LocalBusiness",
      "@id": "https://www.extreme-clean.com.au/#organization",
      "name": "Extreme Clean Australia"
    },
    "areaServed": [
      "Perth", "Osborne Park", "Malaga", "Kewdale", "Welshpool", "Canning Vale", 
      "Bibra Lake", "Jandakot", "Wangara", "Balcatta", "Bayswater", "Rockingham", 
      "Mandurah", "Joondalup", "Midland", "Yanchep", "Leederville", "Northbridge", 
      "Mount Lawley", "Fremantle", "Cockburn", "Landsdale", "Hope Valley", 
      "Kalgoorlie", "Esperance", "Albany", "Busselton", "Bunbury", "Margaret River", "Australind", "Geraldton"
    ],
    "description": "Warehouse and industrial sites need a different kind of clean to a standard commercial property, concrete floors, painted line markings, dust, oil and grease build-up, and large open areas that take real equipment and experience to get right. Extreme Clean Australia handles commercial end-of-lease warehouse turnovers and ongoing industrial cleaning across WA, backed by 20+ years of experience across major commercial and government sites."
  };

  const relatedServices = [
    {
      title: 'Commercial Office Cleaning',
      description: 'Full office cleaning to keep occupied sites presentable day to day, or as part of a warehouse-to-office fitout handover.',
      link: '/services/commercial-office-cleaning-perth',
      icon: Building2
    },
    {
      title: 'Commercial Window Cleaning',
      description: 'Interior and exterior window cleaning, often bundled into an end-of-lease warehouse turnover.',
      link: '/services/commercial-window-cleaning-perth',
      icon: Square
    },
    {
      title: 'Commercial Carpet Cleaning',
      description: 'Deep carpet cleaning for any carpeted office or amenity areas within a warehouse or industrial site.',
      link: '/services/commercial-carpet-cleaning-perth',
      icon: Sofa
    }
  ];

  return (
    <>
      <Helmet>
        <link rel="canonical" href="https://www.extreme-clean.com.au/services/warehouse-industrial-cleaning-perth" />
        <title>Warehouse & Industrial Cleaning Perth | Extreme Clean Australia</title>
        <meta name="description" content="Commercial end-of-lease warehouse turnovers and ongoing industrial cleaning across Perth and WA. High-pressure cleaning, concrete floor cleaning, paint removal. 20+ years experience." />
      </Helmet>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }} />

      <div className="pt-24 lg:pt-44" data-testid="warehouse-industrial-page">
        {/* Hero - Full width background style */}
        <section className="bg-[#0A0A0A] py-24 relative" data-testid="service-hero">
          <div className="absolute inset-0">
            <img 
              src="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?crop=entropy&cs=srgb&fm=jpg&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&ixlib=rb-4.1.0&q=85"
              alt="Industrial warehouse interior"
              className="w-full h-full object-cover opacity-30"
            />
          </div>
          <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 bg-[#3B82F6] bg-opacity-20 px-4 py-2 mb-6">
                <Factory size={20} className="text-[#3B82F6]" />
                <span className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider">Industrial Cleaning</span>
              </div>
              <h1 className="font-heading font-bold text-4xl md:text-5xl lg:text-6xl !leading-[1.2] mb-6 text-white">
                Warehouse & Industrial Cleaning Perth
              </h1>
              <p className="text-xl text-gray-300 mb-4">
                Commercial end-of-lease and industrial cleaning built for handovers, inspections and ongoing operations
              </p>
              <p className="text-gray-400 mb-8">
                Warehouse and industrial sites need a different kind of clean to a standard commercial property, concrete floors, painted line markings, dust, oil and grease build-up, and large open areas that take real equipment and experience to get right. Extreme Clean Australia handles commercial end-of-lease warehouse turnovers and ongoing industrial cleaning across WA, backed by 20+ years of experience across major commercial and government sites.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={onQuoteClick}
                  className="bg-[#3B82F6] hover:bg-blue-600 text-white uppercase font-bold tracking-widest px-8 py-4 text-sm transition-all flex items-center justify-center gap-2"
                >
                  Get Free Quote <ArrowRight size={18} />
                </button>
                <a
                  href="tel:0861875523"
                  className="border-2 border-white text-white hover:bg-white hover:text-[#0A0A0A] transition-all uppercase font-bold tracking-widest px-8 py-4 text-sm flex items-center justify-center gap-2"
                >
                  <Phone size={18} /> Call 08 6187 5523
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* What's Included */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">Scope</p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-6">What's Included</h2>
              <p className="text-gray-600 mb-8">
                A warehouse or industrial clean is scoped around the site and the reason for the job. Commercial end-of-lease warehouse turnovers typically cover high-pressure cleaning of floors and hard surfaces, rotary concrete floor cleaning, removal of painted line markings, paint removal, and clearing built-up dust, oil and grease from the space. We also handle commercial carpet cleaning, toilet and amenity cleaning, window detailing, and carpark cleans as part of a full handover, everything a site needs to be handed back properly.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  'High-pressure floor cleaning',
                  'Rotary concrete floor cleaning',
                  'Paint & line marking removal',
                  'Dust, oil & grease removal',
                  'Carpet & amenity cleaning',
                  'Window & carpark cleaning'
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <Check size={20} className="text-[#3B82F6] flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Commercial End-of-Lease */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">End of Lease</p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-6">Commercial End-of-Lease Warehouse Turnovers</h2>
              <p className="text-gray-600">
                When a warehouse lease ends, the space needs to go back to the landlord or agent in the condition the lease requires, not just swept out. That means concrete floors properly cleaned (not just mopped), old line markings and paint removed, dust and oil residue cleared, and everything from carpet to windows to the carpark handled in one coordinated job. We scope this work around the property and the handover timeline, not a generic checklist.
              </p>
            </div>
          </div>
        </section>

        {/* Ongoing Industrial Cleaning */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">Recurring Services</p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-6">Ongoing Industrial Cleaning</h2>
              <p className="text-gray-600">
                Beyond one-off turnovers, we also provide ongoing exterior high-pressure cleaning for operating commercial sites, including regular high-pressure cleaning for KFC locations across WA. Whether it's a single end-of-lease job or a recurring exterior clean, the same standard applies.
              </p>
            </div>
          </div>
        </section>

        {/* Building Toward Government and Mining */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">Future Direction</p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-6">Building Toward Government and Mining Work</h2>
              <p className="text-gray-600">
                We're actively working toward ISO certification to take on larger government and mining contracts, this is a genuine priority for the business, not a claim we're making before we're ready. In the meantime, we already provide commercial and industrial cleaning for local government sites, and we're building the certifications and processes needed to take that further.
              </p>
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-20 bg-[#273268]">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">Why Us</p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-6 text-white">Why Businesses Choose Extreme Clean Australia</h2>
              <p className="text-gray-300">
                Industrial and warehouse sites don't forgive shortcuts, concrete floors, oil residue and painted line markings need the right equipment and real experience to clean properly, not just a standard mop and bucket approach. Backed by 20+ years working across commercial, food-service and government sites in WA, we bring the same level of care to a warehouse floor as we do to a compliance-critical kitchen exhaust system. We're especially active across Perth's industrial areas, including Osborne Park, Wangara, Malaga, Welshpool, Landsdale and Hope Valley.
              </p>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">FAQ</p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-8">Common Questions</h2>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <div 
                    key={index} 
                    className="border border-gray-200 bg-gray-50"
                  >
                    <button
                      onClick={() => setOpenFaq(openFaq === index ? null : index)}
                      className="w-full px-6 py-4 flex items-center justify-between text-left"
                      aria-expanded={openFaq === index}
                    >
                      <span className="font-heading font-bold text-lg pr-4">{faq.q}</span>
                      {openFaq === index ? (
                        <ChevronUp size={20} className="text-[#3B82F6] flex-shrink-0" />
                      ) : (
                        <ChevronDown size={20} className="text-gray-400 flex-shrink-0" />
                      )}
                    </button>
                    <div 
                      className={`overflow-hidden transition-all duration-300 ${openFaq === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}
                    >
                      <div className="px-6 pb-4">
                        <p className="text-gray-600">{faq.a}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Areas We Service */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <div className="flex items-center gap-3 mb-2">
                <MapPin size={20} className="text-[#3B82F6]" />
                <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider">Service Areas</p>
              </div>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-6">Areas We Service</h2>
              
              <div className="space-y-6">
                <div>
                  <h3 className="font-heading font-bold text-xl mb-3">Perth Metro</h3>
                  <p className="text-gray-600">
                    We service the entire Perth metro area, from Yanchep in the north to Mandurah in the south, including Osborne Park, Malaga, Kewdale, Welshpool, Canning Vale, Bibra Lake, Jandakot, Wangara, Balcatta, Bayswater, Rockingham, Joondalup and Midland.
                  </p>
                </div>
                
                <div>
                  <h3 className="font-heading font-bold text-xl mb-3">Regional WA</h3>
                  <p className="text-gray-600">
                    Kalgoorlie, Esperance, Albany, Busselton, Bunbury, Margaret River, Australind, Geraldton.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Related Services */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-4xl">
              <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">Related</p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-8">Related Services</h2>
              <div className="grid md:grid-cols-3 gap-6">
                {relatedServices.map((service, index) => (
                  <Link 
                    key={index}
                    to={service.link}
                    className="block bg-gray-50 p-6 border border-gray-200 hover:border-[#3B82F6] hover:shadow-md transition-all group"
                  >
                    <div className="w-12 h-12 bg-blue-50 mb-4 flex items-center justify-center group-hover:bg-[#3B82F6] transition-colors">
                      <service.icon size={24} className="text-[#3B82F6] group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="font-heading font-bold text-lg mb-2 group-hover:text-[#3B82F6] transition-colors">{service.title}</h3>
                    <p className="text-gray-600 text-sm">{service.description}</p>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-[#3B82F6]">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-6">
              Ready to Discuss Your Warehouse Cleaning Needs?
            </h2>
            <p className="text-blue-100 text-lg mb-8">
              Get a free quote for end-of-lease turnovers or ongoing industrial cleaning.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={onQuoteClick}
                className="bg-white text-[#0A0A0A] hover:bg-gray-100 transition-all uppercase font-bold tracking-widest px-8 py-4 text-sm"
              >
                Get Free Quote
              </button>
              <a
                href="tel:0861875523"
                className="border-2 border-white text-white hover:bg-white hover:text-[#3B82F6] transition-all uppercase font-bold tracking-widest px-8 py-4 text-sm flex items-center justify-center gap-2"
              >
                <Phone size={18} /> Call 08 6187 5523
              </a>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default WarehouseIndustrialCleaning;
